import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Image } from "primereact/image";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { ColorPicker } from "primereact/colorpicker";
import {
  getSetupDetails,
  updateHeroSection,
  getBannerImages,
  uploadBannerImage,
  uploadBannerImageAndGetUrl,
  deleteBannerImage,
  updateHomeSection,
  updateShopHeroSection,
} from "../../../store/AsyncMethods/SetupMethod";
import { TabView, TabPanel } from "primereact/tabview";
import { Checkbox } from "primereact/checkbox";
import { toast } from "react-toastify";
import { SelectButton } from "primereact/selectbutton";

export default function ManageHeroSection() {
  const dispatch = useDispatch();
  const [activeIndex, setActiveIndex] = useState(0);
  const [heroImageUrl, setHeroImageUrl] = useState(null);
  const [heroImage, setHeroImage] = useState(null);
  const heroUploadRef = useRef(null);
  const shopHeroFileInputRef = useRef(null);
  const [shopHeroUploading, setShopHeroUploading] = useState(false);

  const navigate = useNavigate();
  const { setupDetails, bannerImages } = useSelector((state) => state.SetupReducer);

  const onHeroImageUpload = () => {
    if (heroUploadRef.current) {
      const inputElement = heroUploadRef.current.getInput();
      inputElement?.click();
    }
  };

  const onHeroImageDelete = (id) => {
    dispatch(deleteBannerImage(id)).then((success) => {
      if (success) {
        dispatch(getBannerImages());
        // close preview if the deleted image was open
        if (selectedImage && selectedImage.id === id) {
          setSelectedImage(null);
          setPreviewVisible(false);
        }
      }
    });
  };

  const onShopHeroFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp", "image/gif", "image/bmp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Unsupported file type. Please upload PNG, JPEG, SVG, WEBP, GIF, or BMP.");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB.");
      e.target.value = "";
      return;
    }

    setShopHeroUploading(true);
    e.target.value = "";
    const formData = new FormData();
    formData.append("banner", file);
    const url = await dispatch(uploadBannerImageAndGetUrl(formData));
    if (url) {
      shopHeroFormik.setFieldValue("shopHeroImage", url);
      toast.success("Image uploaded successfully.");
    } else {
      toast.error("Failed to upload image.");
    }
    setShopHeroUploading(false);
  };

  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const openPreview = (image) => {
    setSelectedImage(image);
    setPreviewVisible(true);
  };

  const closePreview = () => {
    setSelectedImage(null);
    setPreviewVisible(false);
  };

  const onHeroImageSelect = (e) => {
    const file = e.files[0];

    if (!file) {
      toast.error("File is invalid or exceeding size");
      return;
    }
    const formData = new FormData();
    formData.append("banner", file);
    dispatch(uploadBannerImage(formData)).then((success) => {
      if (success) {
        dispatch(getBannerImages());
      }
    });
    //setHeroImageUrl(file);

   // const url = URL.createObjectURL(file);
   // setHeroImage(url);
  };

  const statusOptions = [
    { name: "Enabled", value: 1 },
    { name: "Disabled", value: 0 },
  ];

  useEffect(() => {
    if (setupDetails) {
      const parseSafeNumber = (v) => {
        if (v === null || typeof v === 'undefined' || v === '') return 0;
        try {
          const cleaned = String(v).replace(/[^0-9.-]/g, '');
          const n = Number(cleaned);
          return Number.isFinite(n) ? n : 0;
        } catch (e) {
          return 0;
        }
      };

      formik.setValues({
        heroHeading1: setupDetails?.hero_h1 || "",
        heroHeading2: setupDetails?.hero_h2 || "",
        heroPlaceHolder: setupDetails?.hero_placeHolder || "",
        topHeaderBanner: setupDetails?.header_banner || "",
        heroHeading1Enable: setupDetails?.hero_h1_enabled ?? 1,
        heroHeading2Enable: setupDetails?.hero_h2_enabled ?? 1,
        heroPlaceHolderEnable: setupDetails?.hero_placeHolder_enabled ?? 1,
        // banner color settings
        headerBannerBgColor: setupDetails?.header_banner_bg_color || "#fff9e6",
        headerBannerTextColor: setupDetails?.header_banner_text_color || "#b46900",
        // shipping settings
        // prefer explicit single/multiple columns; fallback to shipping_charges JSON
        shipping_charges: parseSafeNumber(setupDetails?.shipping_charges ?? 0),
        shipping_charges_single: (typeof setupDetails?.shipping_charges_single !== 'undefined') ? parseSafeNumber(setupDetails.shipping_charges_single) : (() => {
          try {
            const raw = setupDetails?.shipping_charges;
            if (typeof raw === 'string') {
              const parsed = JSON.parse(raw);
              return parsed?.single || 0;
            }
            return (raw && raw.single) || 0;
          } catch (e) {
            return 0;
          }
        })(),
        shipping_charges_multiple: (typeof setupDetails?.shipping_charges_multiple !== 'undefined') ? parseSafeNumber(setupDetails.shipping_charges_multiple) : (() => {
          try {
            const raw = setupDetails?.shipping_charges;
            if (typeof raw === 'string') {
              const parsed = JSON.parse(raw);
              return parsed?.multiple || 0;
            }
            return (raw && raw.multiple) || 0;
          } catch (e) {
            return 0;
          }
        })(),
        free_shipping_enabled: setupDetails?.free_shipping_enabled ?? 0,
        free_shipping_minimum: parseSafeNumber(setupDetails?.free_shipping_minimum ?? 0),
      });
      shopHeroFormik.setValues({
        shopHeroSubtitle: setupDetails?.shop_hero_subtitle || "— CATEGORIES",
        shopHeroTitle: setupDetails?.shop_hero_title || "Explore The Various Collection of Crockery Collection",
        shopHeroDescription: setupDetails?.shop_hero_description || "Discover premium quality dinnerware, serveware, and tableware that brings elegance to your dining experience. Each piece is carefully selected for its craftsmanship and design.",
        shopHeroImage: setupDetails?.shop_hero_image || "https://images.unsplash.com/photo-1578749556568-bc2c40e68b61?w=1600&q=80",
        shopHeroSubtitleEnable: setupDetails?.shop_hero_subtitle_enabled ?? 1,
        shopHeroTitleEnable: setupDetails?.shop_hero_title_enabled ?? 1,
        shopHeroDescriptionEnable: setupDetails?.shop_hero_description_enabled ?? 1,
        shopHeroImageEnable: setupDetails?.shop_hero_image_enabled ?? 1,
      });
    }
  }, [setupDetails]);

  const shopHeroFormik = useFormik({
    initialValues: {
      shopHeroSubtitle: "— CATEGORIES",
      shopHeroTitle: "Explore The Various Collection of Crockery Collection",
      shopHeroDescription: "Discover premium quality dinnerware, serveware, and tableware that brings elegance to your dining experience. Each piece is carefully selected for its craftsmanship and design.",
      shopHeroImage: "https://images.unsplash.com/photo-1578749556568-bc2c40e68b61?w=1600&q=80",
      shopHeroSubtitleEnable: 1,
      shopHeroTitleEnable: 1,
      shopHeroDescriptionEnable: 1,
      shopHeroImageEnable: 1,
    },
    validationSchema: Yup.object({
      shopHeroSubtitle: Yup.string().notRequired(),
      shopHeroTitle: Yup.string().notRequired(),
      shopHeroDescription: Yup.string().notRequired(),
      shopHeroImage: Yup.string().notRequired(),
    }),
    onSubmit: async (data) => {
      console.debug("[ShopHeroSection] submitting payload:", data);
      try {
        const success = await dispatch(updateShopHeroSection(data));
        console.debug("[ShopHeroSection] updateShopHeroSection result:", success);
        if (success) {
          shopHeroFormik.resetForm();
          dispatch(getSetupDetails());
          navigate("/site-setup/hero-section");
        } else {
          toast.error("Failed to save shop hero section");
        }
      } catch (err) {
        console.error("[ShopHeroSection] submit error:", err);
        toast.error("An error occurred while saving");
      }
    },
  });

  const formik = useFormik({
    initialValues: {
      heroHeading1: "",
      heroHeading2: "",
      heroPlaceHolder: "",
      topHeaderBanner: "",
      heroHeading1Enable: 1,
      heroHeading2Enable: 1,
      heroPlaceHolderEnable: 1,
      // banner color settings
      headerBannerBgColor: "#fff9e6",
      headerBannerTextColor: "#b46900",
      // shipping settings
      shipping_charges: 0,
      shipping_charges_single: 0,
      shipping_charges_multiple: 0,
      free_shipping_enabled: 0,
      free_shipping_minimum: 0,
    },
    // Allow hero fields to be optional so admins can disable and save empty values
    validationSchema: Yup.object({
      heroHeading1: Yup.string().notRequired(),
      heroHeading2: Yup.string().notRequired(),
      heroPlaceHolder: Yup.string().notRequired(),
      topHeaderBanner: Yup.string(),
      headerBannerBgColor: Yup.string().notRequired(),
      headerBannerTextColor: Yup.string().notRequired(),
      shipping_charges: Yup.number().min(0).optional(),
      shipping_charges_single: Yup.number().min(0).optional(),
      shipping_charges_multiple: Yup.number().min(0).optional(),
      free_shipping_minimum: Yup.number().min(0).optional(),
    }),

    onSubmit: async (data) => {
      // Map the new UI field `topHeaderBanner` to backend `headerBanner` so we don't change the API
      const payload = {
        ...data,
        headerBanner: data.topHeaderBanner,
      };

      // Ensure numeric fields are safe numbers (avoid NaN when fields are empty)
      const safeNumber = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };

      payload.shipping_charges = safeNumber(payload.shipping_charges);
      payload.shipping_charges_single = safeNumber(payload.shipping_charges_single);
      payload.shipping_charges_multiple = safeNumber(payload.shipping_charges_multiple);
      payload.free_shipping_minimum = safeNumber(payload.free_shipping_minimum);

      // DEVELOPMENT DEBUG: log payload and ensure dispatch is awaited
      // eslint-disable-next-line no-console
      console.debug("[HeroSection] submitting payload:", payload);

      try {
        const success = await dispatch(updateHeroSection(payload));
        // eslint-disable-next-line no-console
        console.debug("[HeroSection] updateHeroSection result:", success);
        if (success) {
          formik.resetForm();
          dispatch(getSetupDetails());
          navigate("/site-setup/hero-section");
        } else {
          toast.error("Failed to save hero section");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[HeroSection] submit error:", err);
        toast.error("An error occurred while saving");
      }
    },
  });

  useEffect(() => {
    dispatch(getSetupDetails());
    dispatch(getBannerImages());
  }, []);

  return (
    <div className="mx-4">
      <h2 className="text-3xl font-bold mb-4 mt-4 text-gray-800">Hero Sections Management</h2>
      <p className="text-gray-600 mb-4">Manage hero sections for different pages of your website</p>
      <div className="card shadow-md rounded-lg p-4 mt-3">
        <style>{`
          .p-tabview .p-tabview-nav {
            border-bottom: 2px solid #dee2e6;
            margin-bottom: 1.5rem;
            background: #f8f9fa;
            padding: 0.5rem 1rem;
            border-radius: 8px 8px 0 0;
          }
          .p-tabview .p-tabview-nav li {
            margin: 0 0.25rem;
          }
          .p-tabview .p-tabview-nav li .p-tabview-nav-link {
            padding: 0.875rem 1.25rem;
            font-weight: 500;
            font-size: 0.9375rem;
            color: #6c757d;
            border: none;
            border-radius: 6px 6px 0 0;
            background: transparent;
            transition: all 0.2s ease-in-out;
            position: relative;
          }
          .p-tabview .p-tabview-nav li .p-tabview-nav-link:hover {
            background: #fff;
            color: #495057;
            box-shadow: 0 -2px 4px rgba(0,0,0,0.05);
          }
          .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
            background: #fff;
            color: var(--primary-color, #3B82F6);
            font-weight: 600;
            box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
          }
          .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: var(--primary-color, #3B82F6);
            border-radius: 3px 3px 0 0;
          }
          .p-tabview .p-tabview-panels {
            background: #fff;
            padding: 1.5rem;
            border-radius: 0 0 8px 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }
        `}</style>
        <TabView
          activeIndex={activeIndex}
          onTabChange={(e) => setActiveIndex(e.index)}
        >
          <TabPanel header="🏠 Home Hero Section">
            <Divider>
              <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
                Customize Home Page Hero Section
              </span>
            </Divider>
            <Button
              onClick={() => {
                navigate("/site-setup");
              }}
              type="button"
              label="Back"
              icon="pi pi-arrow-left"
              className="  p-primary-highlight-btn mb-3"
            />
            <form onSubmit={formik.handleSubmit}>
              <div className="px-4 gap-4 grid grid-cols-1 lg:grid-cols-2">
                <div className="">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="heroHeading1" className="">
                      Heading 1
                    </label>
                <span className="flex  w-full gap-2 justify-center items-center">
                  <InputText
                    id="heroHeading1"
                    name="heroHeading1"
                    maxLength={255}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.heroHeading1}
                    onChange={formik.handleChange}
                  />

                  <SelectButton
                    name="heroHeading1Enable"
                    id="heroHeading1Enable"
                    options={statusOptions}
                    optionLabel="name"
                    optionValue="value"
                    className="flex"
                    pt={{
                      root: { className: "flex" },
                      button: ({ context }) => ({
                        className: context.selected
                          ? "p-primary-highlight-btn w-full text-lg text-center"
                          : "w-full text-lg text-center",
                      }),
                    }}
                    value={formik.values.heroHeading1Enable}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
              {formik.touched?.heroHeading1 && formik.errors?.heroHeading1 && (
                <div className="p-error">{formik.errors?.heroHeading1}</div>
              )}
            </div>

            <div className="">
              <div className=" flex flex-col gap-2">
                <label htmlFor="heroHeading2" className="">
                  Heading 2
                </label>
                <span className="flex  w-full gap-2 items-center justify-center">
                  <InputText
                    id="heroHeading2"
                    name="heroHeading2"
                    maxLength={255}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.heroHeading2}
                    onChange={formik.handleChange}
                  />
                  <SelectButton
                    name="heroHeading2Enable"
                    id="heroHeading2Enable"
                    options={statusOptions}
                    optionLabel="name"
                    optionValue="value"
                    className="flex"
                    pt={{
                      root: { className: "flex" },
                      button: ({ context }) => ({
                        className: context.selected
                          ? "p-primary-highlight-btn w-full text-lg text-center"
                          : "w-full text-lg text-center",
                      }),
                    }}
                    value={formik.values.heroHeading2Enable}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className=" flex flex-col gap-2">
                <label htmlFor="heroPlaceHolder" className="">
                  Search PlaceHolder
                </label>
                <span className="flex  w-full gap-2 items-center justify-center">
                  <InputText
                    id="heroPlaceHolder"
                    name="heroPlaceHolder"
                    maxLength={255}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.heroPlaceHolder}
                    onChange={formik.handleChange}
                  />
                  <SelectButton
                    name="heroPlaceHolderEnable"
                    id="heroPlaceHolderEnable"
                    options={statusOptions}
                    optionLabel="name"
                    optionValue="value"
                    className="flex"
                    pt={{
                      root: { className: "flex" },
                      button: ({ context }) => ({
                        className: context.selected
                          ? "p-primary-highlight-btn w-full text-lg text-center"
                          : "w-full text-lg text-center",
                      }),
                    }}
                    value={formik.values.heroPlaceHolderEnable}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
              {formik.touched?.heroPlaceHolder &&
                formik.errors?.heroPlaceHolder && (
                  <div className="p-error">
                    {formik.errors?.heroPlaceHolder}
                  </div>
                )}
            </div>
            <div className="">
              <div className=" flex flex-col gap-2">
                <label htmlFor="topHeaderBanner" className="">
                  Top Header Banner Text
                </label>
                <span className="flex  w-full gap-2 items-center justify-center">
                  <InputText
                    id="topHeaderBanner"
                    name="topHeaderBanner"
                    maxLength={255}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.topHeaderBanner}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
            </div>

            {/* Banner Color Settings */}
            <div className="">
              <div className="flex flex-col gap-2">
                <label className="">Banner Background Color</label>
                <div className="flex items-center gap-3">
                  <ColorPicker
                    value={formik.values.headerBannerBgColor?.replace('#', '') || 'fff9e6'}
                    onChange={(e) => formik.setFieldValue('headerBannerBgColor', '#' + e.value)}
                    format="hex"
                    className="p-colorpicker-preview"
                  />
                  <InputText
                    value={formik.values.headerBannerBgColor || '#fff9e6'}
                    onChange={(e) => formik.setFieldValue('headerBannerBgColor', e.target.value)}
                    className="w-32 text-sm p-primary-input"
                    placeholder="#fff9e6"
                  />
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 6,
                      backgroundColor: formik.values.headerBannerBgColor || '#fff9e6',
                      border: '1px solid #ddd'
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label className="">Banner Text Color</label>
                <div className="flex items-center gap-3">
                  <ColorPicker
                    value={formik.values.headerBannerTextColor?.replace('#', '') || 'b46900'}
                    onChange={(e) => formik.setFieldValue('headerBannerTextColor', '#' + e.value)}
                    format="hex"
                    className="p-colorpicker-preview"
                  />
                  <InputText
                    value={formik.values.headerBannerTextColor || '#b46900'}
                    onChange={(e) => formik.setFieldValue('headerBannerTextColor', e.target.value)}
                    className="w-32 text-sm p-primary-input"
                    placeholder="#b46900"
                  />
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 6,
                      backgroundColor: formik.values.headerBannerTextColor || '#b46900',
                      border: '1px solid #ddd'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Preview (part of hero customization) */}
            <div className="mt-3 col-span-1 lg:col-span-2">
              <label className="mb-2">Preview</label>
              <div className="p-4 bg-white rounded-md shadow-sm">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
                  <div className="flex flex-col justify-center">
                    {/* Left side hero preview: Heading1 big, Heading2 smaller */}
                    {formik.values.heroHeading1 && formik.values.heroHeading1Enable === 1 && (
                      <h2 className="text-3xl font-bold mb-2">{formik.values.heroHeading1}</h2>
                    )}
                    {formik.values.heroHeading2 && formik.values.heroHeading2Enable === 1 && (
                      <p className="text-lg text-gray-600">{formik.values.heroHeading2}</p>
                    )}
                  </div>
                  <div>
                    <div className="searchWrapper bg-white shadow p-3 rounded">
                      <input
                        type="text"
                        className="form-control w-full"
                        placeholder={formik.values.heroPlaceHolderEnable === 1 ? formik.values.heroPlaceHolder || 'Search products...' : 'Search products...'}
                        readOnly
                      />
                    </div>
                    <div className="mt-3" style={{ background: formik.values.headerBannerBgColor || '#fff9e6', padding: '0.5rem', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', gap: '1rem', whiteSpace: 'nowrap', animation: 'previewMarquee 12s linear infinite' }}>
                        <div style={{ color: formik.values.headerBannerTextColor || '#b46900', fontWeight: 600 }}>{formik.values.topHeaderBanner}</div>
                        <div style={{ color: formik.values.headerBannerTextColor || '#b46900', fontWeight: 600 }}>{formik.values.topHeaderBanner}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <style>{`@keyframes previewMarquee{0%{transform:translateX(0);}100%{transform:translateX(-50%);}}`}</style>
            </div>

            {/* Hero Image (part of hero customization) */}
            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="heroImage" className="">
                  Hero Image
                </label>
                <div className=" flex flex-col gap-2">
                  <Button
                    onClick={onHeroImageUpload}
                    type="button"
                    label="Upload"
                    icon="pi pi-plus"
                    className="  p-primary-highlight-btn"
                  />

                  <FileUpload
                    ref={heroUploadRef}
                    mode="basic"
                    name="hero-image-upload"
                    accept="image/*"
                    maxFileSize={5000000}
                    auto
                    onSelect={onHeroImageSelect}
                    style={{ display: "none" }}
                  />
                  <small id="hero--image-upload-help">
                    Please upload a supported image file smaller than 5MB.1920px × 1080px 16:9 aspect ratio
                  </small>

                  <div className="flex flex-row-reverse">
                    <div className=" w-[128px] h-[128px] overflow-y-scroll">
                      {bannerImages?.map((image) => (
                        <div key={image.id} className="flex flex-row gap-2">
                          <button type="button" onClick={() => openPreview(image)} className="p-0 border-0 bg-transparent">
                            <Image
                              src={image.image_path}
                              alt="Uploaded Hero Image"
                              width="128"
                              height="128"
                              preview={false}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {formik.touched?.heroImage && formik.errors?.heroImage && (
                <div className="p-error">{formik.errors?.heroImage}</div>
              )}
            </div>

            {/* Shipping settings grouped in a dedicated section */}
            <div className="col-span-1 lg:col-span-2">
              <Divider>
                <span className="text-xl font-semibold p-font-style text-center text-black mx-1">
                  Shipping Setup
                </span>
              </Divider>

              <div className="p-4 bg-white rounded-md shadow-sm">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="shipping_charges" className="">
                        Shipping Charges
                      </label>
                      <span className=" w-full">
                        <InputNumber
                          id="shipping_charges"
                          name="shipping_charges"
                          mode="currency"
                          currency="PKR"
                          locale="en-PK"
                          minFractionDigits={0}
                          maxFractionDigits={0}
                          className="w-full text-lg p-primary-input"
                          value={formik.values.shipping_charges}
                          onValueChange={(e) => {
                            const val = e?.value;
                            formik.setFieldValue('shipping_charges', Number.isFinite(val) ? val : 0);
                          }}
                        />
                      </span>
                    </div>
                  </div>

                  <div className="">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="shipping_charges_single" className="">
                        Single Item Shipping Charges
                      </label>
                      <span className=" w-full">
                        <InputNumber
                          id="shipping_charges_single"
                          name="shipping_charges_single"
                          mode="decimal"
                          minFractionDigits={0}
                          maxFractionDigits={0}
                          className="w-full text-lg p-primary-input"
                          value={formik.values.shipping_charges_single}
                          onValueChange={(e) => {
                            const val = e?.value;
                            formik.setFieldValue('shipping_charges_single', Number.isFinite(val) ? val : 0);
                          }}
                          placeholder="300"
                        />
                      </span>
                    </div>
                  </div>

                  <div className="">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="shipping_charges_multiple" className="">
                        Multiple Items Shipping Charges
                      </label>
                      <span className=" w-full">
                        <InputNumber
                          id="shipping_charges_multiple"
                          name="shipping_charges_multiple"
                          mode="decimal"
                          minFractionDigits={0}
                          maxFractionDigits={0}
                          className="w-full text-lg p-primary-input"
                          value={formik.values.shipping_charges_multiple}
                          onValueChange={(e) => {
                            const val = e?.value;
                            formik.setFieldValue('shipping_charges_multiple', Number.isFinite(val) ? val : 0);
                          }}
                          placeholder="450"
                        />
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="free_shipping_enabled" className="">
                        Free Shipping Enabled
                      </label>
                      <span className=" w-full">
                        <SelectButton
                          name="free_shipping_enabled"
                          id="free_shipping_enabled"
                          options={statusOptions}
                          optionLabel="name"
                          optionValue="value"
                          value={formik.values.free_shipping_enabled}
                          onChange={(e) => formik.setFieldValue('free_shipping_enabled', e.value)}
                        />
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="free_shipping_minimum" className="">
                        Free Shipping Minimum
                      </label>
                      <span className=" w-full">
                        <InputNumber
                          id="free_shipping_minimum"
                          name="free_shipping_minimum"
                          mode="currency"
                          currency="PKR"
                          locale="en-PK"
                          minFractionDigits={0}
                          maxFractionDigits={0}
                          className="w-full text-lg p-primary-input"
                          value={formik.values.free_shipping_minimum}
                          onValueChange={(e) => {
                            const val = e?.value;
                            formik.setFieldValue('free_shipping_minimum', Number.isFinite(val) ? val : 0);
                          }}
                        />
                      </span>
                    </div>
                  </div>
                </div>

                <p className="mt-3 text-sm text-gray-600">
                  When free shipping is enabled, orders with total value greater than or equal to the minimum will get shipping for free.
                </p>
              </div>
            </div>

          </div>
              <div className="mt-8">
                  <div className="flex justify-end gap-4">
                  <Button
                    label="Clear"
                    icon="pi pi-times"
                    className="p-red-btn"
                    type="button"
                    onClick={() => {
                      formik.resetForm();
                    }}
                  />
                  <Button
                    type="submit"
                    label="Save"
                    icon="pi pi-check"
                    className="p-secondary-btn"
                    onClick={() => {
                      // eslint-disable-next-line no-console
                      console.debug('[HeroSection] Save clicked', formik.values);
                      formik.submitForm();
                    }}
                  />
                </div>
              </div>
            </form>
          </TabPanel>
          <TabPanel header="⚙️ Home Page Settings">
            <Divider>
              <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
                Manage Home Page Settings
              </span>
            </Divider>
            <div className="px-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  inputId="show_featured_home_essentials"
                  checked={!!(+setupDetails?.show_featured_home_essentials)}
                  onChange={(e) => {
                    const nextVal = e.checked;
                    const payload = { show_featured_home_essentials: nextVal ? 1 : 0 };
                    dispatch(updateHomeSection(payload)).then((success) => {
                      if (success) {
                        dispatch(getSetupDetails());
                        toast.success("Home section visibility updated");
                      } else {
                        toast.error("Failed to update home section visibility");
                      }
                    });
                  }}
                />
                <label htmlFor="show_featured_home_essentials" className="text-lg">
                  Is feature section show
                </label>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Toggle this to show or hide the featured products section on the homepage.
              </p>
            </div>
          </TabPanel>
          <TabPanel header="🛍️ Shop Hero Section">
            <Divider>
              <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
                Customize Shop Page Hero Section
              </span>
            </Divider>
            <Button
              onClick={() => {
                navigate("/site-setup");
              }}
              type="button"
              label="Back"
              icon="pi pi-arrow-left"
              className="p-primary-highlight-btn mb-3"
            />
            <form onSubmit={shopHeroFormik.handleSubmit}>
              <div className="px-4 gap-4 grid grid-cols-1 lg:grid-cols-2">
                <div className="">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="shopHeroSubtitle" className="">
                      Subtitle
                    </label>
                    <span className="flex w-full gap-2 justify-center items-center">
                      <InputText
                        id="shopHeroSubtitle"
                        name="shopHeroSubtitle"
                        maxLength={255}
                        className="w-full text-lg p-primary-input"
                        value={shopHeroFormik.values.shopHeroSubtitle}
                        onChange={shopHeroFormik.handleChange}
                      />
                      <SelectButton
                        name="shopHeroSubtitleEnable"
                        id="shopHeroSubtitleEnable"
                        options={statusOptions}
                        optionLabel="name"
                        optionValue="value"
                        className="flex"
                        pt={{
                          root: { className: "flex" },
                          button: ({ context }) => ({
                            className: context.selected
                              ? "p-primary-highlight-btn w-full text-lg text-center"
                              : "w-full text-lg text-center",
                          }),
                        }}
                        value={shopHeroFormik.values.shopHeroSubtitleEnable}
                        onChange={shopHeroFormik.handleChange}
                      />
                    </span>
                  </div>
                </div>

                <div className="">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="shopHeroTitle" className="">
                      Title
                    </label>
                    <span className="flex w-full gap-2 items-center justify-center">
                      <InputText
                        id="shopHeroTitle"
                        name="shopHeroTitle"
                        maxLength={500}
                        className="w-full text-lg p-primary-input"
                        value={shopHeroFormik.values.shopHeroTitle}
                        onChange={shopHeroFormik.handleChange}
                      />
                      <SelectButton
                        name="shopHeroTitleEnable"
                        id="shopHeroTitleEnable"
                        options={statusOptions}
                        optionLabel="name"
                        optionValue="value"
                        className="flex"
                        pt={{
                          root: { className: "flex" },
                          button: ({ context }) => ({
                            className: context.selected
                              ? "p-primary-highlight-btn w-full text-lg text-center"
                              : "w-full text-lg text-center",
                          }),
                        }}
                        value={shopHeroFormik.values.shopHeroTitleEnable}
                        onChange={shopHeroFormik.handleChange}
                      />
                    </span>
                  </div>
                </div>

                <div className="col-span-1 lg:col-span-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="shopHeroDescription" className="">
                      Description
                    </label>
                    <span className="flex w-full gap-2 items-center justify-center">
                      <InputText
                        id="shopHeroDescription"
                        name="shopHeroDescription"
                        className="w-full text-lg p-primary-input"
                        value={shopHeroFormik.values.shopHeroDescription}
                        onChange={shopHeroFormik.handleChange}
                      />
                      <SelectButton
                        name="shopHeroDescriptionEnable"
                        id="shopHeroDescriptionEnable"
                        options={statusOptions}
                        optionLabel="name"
                        optionValue="value"
                        className="flex"
                        pt={{
                          root: { className: "flex" },
                          button: ({ context }) => ({
                            className: context.selected
                              ? "p-primary-highlight-btn w-full text-lg text-center"
                              : "w-full text-lg text-center",
                          }),
                        }}
                        value={shopHeroFormik.values.shopHeroDescriptionEnable}
                        onChange={shopHeroFormik.handleChange}
                      />
                    </span>
                  </div>
                </div>

                <div className="col-span-1 lg:col-span-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="shopHeroImage" className="">
                      Background Image
                    </label>
                    {/* URL + Upload button row */}
                    <span className="flex w-full gap-2 items-center justify-center">
                      <InputText
                        id="shopHeroImage"
                        name="shopHeroImage"
                        placeholder="Paste image URL or upload a file below"
                        className="w-full text-lg p-primary-input"
                        value={shopHeroFormik.values.shopHeroImage}
                        onChange={shopHeroFormik.handleChange}
                      />
                      <Button
                        type="button"
                        icon={shopHeroUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
                        label="Upload"
                        className="p-primary-highlight-btn whitespace-nowrap"
                        disabled={shopHeroUploading}
                        onClick={() => shopHeroFileInputRef.current?.click()}
                      />
                      <SelectButton
                        name="shopHeroImageEnable"
                        id="shopHeroImageEnable"
                        options={statusOptions}
                        optionLabel="name"
                        optionValue="value"
                        className="flex"
                        pt={{
                          root: { className: "flex" },
                          button: ({ context }) => ({
                            className: context.selected
                              ? "p-primary-highlight-btn w-full text-lg text-center"
                              : "w-full text-lg text-center",
                          }),
                        }}
                        value={shopHeroFormik.values.shopHeroImageEnable}
                        onChange={shopHeroFormik.handleChange}
                      />
                    </span>
                    {/* Hidden file input */}
                    <input
                      ref={shopHeroFileInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg,.webp,.gif,.bmp,image/*"
                      style={{ display: "none" }}
                      onChange={onShopHeroFileSelect}
                    />
                    <small className="text-gray-500">
                      Supported formats: PNG, JPEG, SVG, WEBP, GIF, BMP — Max 5MB
                    </small>
                    {shopHeroFormik.touched?.shopHeroImage && shopHeroFormik.errors?.shopHeroImage && (
                      <div className="p-error">{shopHeroFormik.errors?.shopHeroImage}</div>
                    )}
                  </div>
                </div>

                {/* Preview */}
                <div className="col-span-1 lg:col-span-2 mt-3">
                  <label className="mb-2">Preview</label>
                  <div className="p-4 bg-gray-100 rounded-md shadow-sm">
                    <div className="relative h-64 rounded overflow-hidden">
                      {shopHeroFormik.values.shopHeroImageEnable === 1 && shopHeroFormik.values.shopHeroImage && (
                        <img
                          src={shopHeroFormik.values.shopHeroImage}
                          alt="Shop Hero Preview"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30"></div>
                      <div className="absolute inset-0 flex items-center justify-start px-8">
                        <div className="text-white">
                          {shopHeroFormik.values.shopHeroSubtitleEnable === 1 && shopHeroFormik.values.shopHeroSubtitle && (
                            <span className="text-sm mb-2 block">{shopHeroFormik.values.shopHeroSubtitle}</span>
                          )}
                          {shopHeroFormik.values.shopHeroTitleEnable === 1 && shopHeroFormik.values.shopHeroTitle && (
                            <h1 className="text-3xl font-bold mb-2">{shopHeroFormik.values.shopHeroTitle}</h1>
                          )}
                          {shopHeroFormik.values.shopHeroDescriptionEnable === 1 && shopHeroFormik.values.shopHeroDescription && (
                            <p className="text-sm">{shopHeroFormik.values.shopHeroDescription}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex justify-end gap-4">
                  <Button
                    label="Clear"
                    icon="pi pi-times"
                    className="p-red-btn"
                    type="button"
                    onClick={() => {
                      shopHeroFormik.resetForm();
                    }}
                  />
                  <Button
                    type="submit"
                    label="Save"
                    icon="pi pi-check"
                    className="p-secondary-btn"
                    onClick={() => {
                      console.debug('[ShopHeroSection] Save clicked', shopHeroFormik.values);
                      shopHeroFormik.submitForm();
                    }}
                  />
                </div>
              </div>
            </form>
          </TabPanel>
        </TabView>

        {/* Preview Dialog for images */}
        <Dialog header="Banner Image Preview" visible={previewVisible} style={{ width: '50vw' }} onHide={closePreview} modal>
          {selectedImage ? (
            <div className="flex flex-col items-center gap-4">
              <img src={selectedImage.image_path} alt="Preview" style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: 6 }} />
              <div className="flex gap-3">
                <Button label="Delete" icon="pi pi-trash" className="p-button-danger" onClick={() => { onHeroImageDelete(selectedImage.id); }} />
                <Button label="Close" icon="pi pi-times" className="p-button-secondary" onClick={closePreview} />
              </div>
            </div>
          ) : null}
        </Dialog>
      </div>
    </div>
  );
}
